import { Event } from "../events/Event";
import { Point } from "../maths/Point";
import { Rectangle } from "../maths/Rectangle";
import { ILaya } from "../../ILaya";
import { Ease } from "../tween/Ease";
import { Tween } from "../tween/Tween";
export class DragSupport {
    constructor(owner) {
        this.ratio = 0.92;
        this.maxOffset = 60;
        this.hasInertia = false;
        this.elasticDistance = 0;
        this.elasticBackTime = 300;
        this.autoStart = false;
        this._testing = false;
        this._dragging = false;
        this.target = owner;
        this.area = new Rectangle();
        this._points = [];
        this.target.on(Event.MOUSE_DOWN, this, this.onMouseDown);
    }
    get dragging() {
        return this._dragging;
    }
    start(data) {
        this.reset();
        this._touchId = ILaya.InputManager.lastTouchId;
        this._dragging = true;
        this._testing = false;
        this._elasticRateX = this._elasticRateY = 1;
        let pt = this.target._parent.getMousePoint();
        this._points.length = 0;
        this._points.push(pt.x, pt.y, performance.now());
        this._data = data;
        ILaya.stage.on(Event.MOUSE_MOVE, this, this.onMouseMove);
        ILaya.stage.on(Event.MOUSE_UP, this, this.onMouseUp);
    }
    reset() {
        this._dragging = false;
        this._testing = false;
        this._data = null;
        ILaya.systemTimer.clear(this, this.tweenMove);
        ILaya.stage.off(Event.MOUSE_MOVE, this, this.onMouseMove);
        ILaya.stage.off(Event.MOUSE_UP, this, this.onMouseUp);
        if (this._tween != null) {
            this._tween.kill();
            this._tween = null;
        }
    }
    stop() {
        if (this._dragging) {
            this._dragging = false;
            if (!this._testing) {
                this.moveTarget(0, 0);
                this.clear(true);
            }
            else
                this.reset();
        }
        else
            this.reset();
    }
    onMouseDown() {
        if (this.autoStart && !this._dragging && !this._testing) {
            this.start();
            this._testing = true;
        }
    }
    onMouseMove(evt) {
        if (!this._testing && !this._dragging)
            return;
        if (this._touchId != evt.touchId)
            return;
        let point = this.target._parent.getMousePoint();
        let mouseX = point.x;
        let mouseY = point.y;
        let offsetX = mouseX - this._points[this._points.length - 3];
        let offsetY = mouseY - this._points[this._points.length - 2];
        if (this._testing) {
            if (Math.abs(offsetX * ILaya.stage._canvasTransform.getScaleX()) < 1 && Math.abs(offsetY * ILaya.stage._canvasTransform.getScaleY()) < 1)
                return;
            this._dragging = true;
            this.target.event(Event.DRAG_START, this._data);
            if (!this._dragging)
                return;
            this._testing = false;
        }
        let now = performance.now();
        while (this._points.length > 0 && this._points[2] < now - 100)
            this._points.splice(0, 3);
        this._points.push(mouseX, mouseY, now);
        this.moveTarget(offsetX * this._elasticRateX, offsetY * this._elasticRateY);
        this.target.event(Event.DRAG_MOVE, this._data);
    }
    onMouseUp(evt) {
        if (!this._dragging) {
            this.reset();
            return;
        }
        if (this._touchId != evt.touchId)
            return;
        if (this.hasInertia) {
            let v = DragSupport.computeVelocity(this._points, this.maxOffset);
            this._points[0] = v.x;
            this._points[1] = v.y;
            this._dragging = false;
            ILaya.systemTimer.frameLoop(1, this, this.tweenMove);
        }
        else if (!this.area.isEmpty() && this.elasticDistance > 0) {
            this.checkElastic();
        }
        else {
            this.clear();
        }
    }
    moveTarget(dx, dy) {
        let nx = this.target._x + dx;
        let ny = this.target._y + dy;
        if (this.area.isEmpty())
            this.target.pos(nx, ny);
        else if (this.elasticDistance > 0 && this._dragging) {
            this.target.pos(nx, ny);
            let pt = this.checkArea();
            this._elasticRateX = Math.max(0, 1 - (Math.abs(pt.x) / this.elasticDistance));
            this._elasticRateY = Math.max(0, 1 - (Math.abs(pt.y) / this.elasticDistance));
        }
        else {
            let pt = this.checkArea(dx, dy);
            this.target.pos(nx + pt.x, ny + pt.y);
        }
    }
    checkArea(dx = 0, dy = 0) {
        let tx;
        let ty;
        let rect = Rectangle.TEMP.setTo(this.target.x + dx - this.target.pivotX, this.target.y + dy - this.target.pivotY, this.target.width, this.target.height);
        if (rect.x < this.area.x)
            tx = this.area.x - rect.x;
        else if (rect.right > this.area.right)
            tx = this.area.right - rect.right;
        else
            tx = 0;
        if (rect.y < this.area.y)
            ty = this.area.y - rect.y;
        else if (rect.bottom > this.area.bottom)
            ty = this.area.bottom - rect.bottom;
        else
            ty = 0;
        return tmpPoint.setTo(tx, ty);
    }
    checkElastic() {
        let pt = this.checkArea();
        if (pt.x != 0 || pt.y != 0) {
            this._tween = Tween.create(this.target).duration(this.elasticBackTime).ease(Ease.sineOut).then(() => this.clear());
            if (pt.x != 0)
                this._tween.to("x", this.target.x + pt.x);
            if (pt.y != 0)
                this._tween.to("y", this.target.y + pt.y);
        }
        else {
            this.clear();
        }
    }
    tweenMove() {
        let n = Math.pow(this.ratio, ILaya.timer.delta / 16.6);
        let s = this.ratio * (1 - n) / (1 - this.ratio);
        let dx = this._points[0] * s * this._elasticRateX;
        let dy = this._points[1] * s * this._elasticRateY;
        this._points[0] = dx;
        this._points[1] = dy;
        if (dx < 0)
            dx = Math.ceil(dx);
        else
            dx = Math.floor(dx);
        if (dy < 0)
            dy = Math.ceil(dy);
        else
            dy = Math.floor(dy);
        if (Math.abs(dx) < 1 && Math.abs(dy) < 1 || this._elasticRateX < 0.5 || this._elasticRateY < 0.5) {
            ILaya.systemTimer.clear(this, this.tweenMove);
            if (!this.area.isEmpty() && this.elasticDistance > 0)
                this.checkElastic();
            else
                this.clear();
        }
        else {
            this.moveTarget(dx, dy);
            this.target.event(Event.DRAG_MOVE, this._data);
        }
    }
    clear(cancelled) {
        let d = this._data;
        this.reset();
        this.target.event(Event.DRAG_END, [d, cancelled]);
    }
    static computeVelocity(points, max) {
        let now = performance.now();
        while (points.length > 0 && points[2] < now - 100)
            points.splice(0, 3);
        let len = points.length / 3;
        let x = 0, y = 0;
        let t = 0;
        for (let i = 1; i < len; i++) {
            x += points[i * 3] - points[i * 3 - 3];
            y += points[i * 3 + 1] - points[i * 3 - 2];
            t += points[i * 3 + 2] - points[i * 3 - 1];
        }
        if (t != 0) {
            t = t / 16.6;
            x = x / t;
            y = y / t;
        }
        else
            x = y = 0;
        if (max != null) {
            if (Math.abs(x) > max)
                x = x > 0 ? max : -max;
            if (Math.abs(y) > max)
                y = y > 0 ? max : -max;
        }
        tmpPoint.setTo(x, y);
        return tmpPoint;
    }
}
const tmpPoint = new Point();
